import React, {useState} from 'react';
import { Trans, useTranslation } from "react-i18next";
import { FavoritesTree, FlatTree, Sport, Tree, TreeSettings } from '../../models/api/codegen/code';
import {useDispatch, useSelector} from "react-redux";
import {LiveOrLineType, StoreType} from "../../store/spirs/types";
import {ReactComponent as FavoriteIcon} from '../icons/favorites.svg';
import {TreeDirectory} from "../tree/Tree";
import {getFavoritesTreeAsync} from "../../store/spirs/actions";
import {RoundedTabs} from "../tabs/RoundedTabs";

type TabType = 'all' | 'live' | 'line';
const icon = <FavoriteIcon className="inline-block flex-shrink-0" />;

export const Favorites = () => {
    const [currentTab, setCurrentTab] = useState<TabType>('all');
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {spirs: {favorites: tree}} = useSelector((state: StoreType) => state);
    React.useEffect(() => {
        dispatch(getFavoritesTreeAsync.request({}));
        const job = setInterval(() => dispatch(getFavoritesTreeAsync.request({})), 5000);
        return () => clearInterval(job);
    }, [currentTab]);

    function handleChange(newValue: TabType) {
        setCurrentTab(newValue);
    }

    const tabs = [
        {value: 'all' as TabType, label: t('results.all.tabTitle')},
        {value: 'live' as TabType, label: t('results.live.tabTitle')},
        {value: 'line' as TabType, label: t('results.line.tabTitle')},
    ];



    return (
        <div>
            <RoundedTabs<TabType>
                tabs={tabs}
                value={currentTab}
                onChange={handleChange}
            />

            {(tree.count === 0 && currentTab === 'all') && (
              <span className="block mt-[1vh] text-[12px] tablet:text-[16px] tablet:px-[10px]">
                  <Trans i18nKey="favorites.test" components={{ 1: icon }}></Trans>
              </span>
            )}
            {(currentTab === 'all' || currentTab === 'live') && tree.live.l.length > 0 && <Group
                text={"favorites.live"}
                mode={"LIVE"}
                sports={tree.live}
                markets={tree.markets}
                v={tree.v}
                settings={tree.settings}
                tab={"live"}
                currentTab={currentTab}
                fixtureWithMarkets={tree.fixtureWithMarkets}
                matchStatuses={tree.matchStatuses}
                soccerStatistics={tree.soccerStatistics}
                alreadyStarted={tree.alreadyStarted}
            />}
            {(currentTab === 'all' || currentTab === 'line') && tree.line.l.length > 0 && <Group
                text={"favorites.line"}
                mode={"LINE"}
                sports={tree.line}
                markets={tree.markets}
                v={tree.v}
                settings={tree.settings}
                tab={"line"}
                currentTab={currentTab}
                fixtureWithMarkets={tree.fixtureWithMarkets}
                matchStatuses={tree.matchStatuses}
                soccerStatistics={tree.soccerStatistics}
                alreadyStarted={tree.alreadyStarted}
            />}
        </div>
    );

};

const CheckTab: React.FC<React.PropsWithChildren<{ condition: boolean }>> = ({condition, children}) => {
    if (!condition) return null;
    return (<>{children}</>)
};

const GamesHeader: React.FC<{ text: string}> = (props) => {
    const {t} = useTranslation();
    return (
        <h1 className="text-[28px] font-medium text-white">
            {t(props.text)}
        </h1>
    );
};

interface GroupProps {
    text: string;
    sports: FlatTree;
    markets: FavoritesTree['markets'];
    settings: TreeSettings;
    v: number;
    mode: LiveOrLineType;
    tab: TabType;
    currentTab: TabType;
    fixtureWithMarkets: Array<number>;
    matchStatuses: FavoritesTree['matchStatuses'];
    soccerStatistics: FavoritesTree['soccerStatistics'];
    alreadyStarted: FavoritesTree['alreadyStarted']
}

const Group: React.FC<GroupProps> = (props) => {
    const {
        text,
        sports,
        settings,
        markets,
        v,
        mode,
        tab,
        currentTab,
        fixtureWithMarkets,
        matchStatuses,
        soccerStatistics,
        alreadyStarted
    } = props;
    const show = ['all', tab].indexOf(currentTab) > -1;
    if ((!sports.s || Object.keys(sports.s).length === 0) && currentTab === tab) {
        return <span className="block mt-[1vh] text-[12px] tablet:text-[16px] tablet:px-[10px]">
                  <Trans i18nKey="favorites.test" components={{ 1: icon }}></Trans>
              </span>;
    }
    const tree: FlatTree = {...sports, v, st: settings, m: markets, f: fixtureWithMarkets, ms: matchStatuses, ss: soccerStatistics, alrds: alreadyStarted};

    return (
        <CheckTab condition={show}>
            {Object.keys(tree.fixtures).length > 0 && <GamesHeader {...{text}}/>}
            {<TreeDirectory tree={tree} mode={mode} fullHeight={true}/>}
        </CheckTab>
    )
};

export {Favorites as default};
